import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useApp } from './Context';
import Loader from '../components/Loader';

function ChartDisplay() {
    const { isLoading, displayData, selectedFilter } = useApp();

    if (isLoading || !displayData.length) {
        return <Loader />;
    }

    // Determine if we have single-line or multi-line data
    const isMultiLine = displayData.length > 0 && typeof displayData[0].value === 'object';

    // Prepare data for the chart
    const chartData = displayData.map(item => ({
        year: item.year,
        ...(isMultiLine ? item.value : { value: item.value })
    }));

    // Define colors for different line types
    const colors = {
        // Spotlight colors
        green: '#22c55e',
        yellow: '#eab308',
        red: '#ef4444',
        // Controversy colors
        deplatformings: '#8b5cf6', // Purple
        scholars_under_fire: '#f59e0b', // Orange
        students_under_fire: '#ef4444', // Red
        // Default
        value: '#3b82f6' // Blue for single line
    };

    // Get line names for multi-line charts
    const getLineNames = () => {
        if (!isMultiLine) return [];
        return Object.keys(chartData[0]).filter(key => key !== 'year');
    };

    // Get display name for line
    const getDisplayName = (key) => {
        const displayNames = {
            green: 'Green Light',
            yellow: 'Yellow Light',
            red: 'Red Light',
            deplatformings: 'Campus Deplatformings',
            scholars_under_fire: 'Scholars Under Fire',
            students_under_fire: 'Students Under Fire'
        };
        return displayNames[key] || key;
    };

    // Get Y-axis domain based on the selected filter
    const getYAxisDomain = () => {
        switch (selectedFilter) {
            case 'average_score':
                return [55, 65];
            case 'speech_controversies':
                return [0, 300];
            case 'spotlight_rating':
                return [0, 350];
            default:
                return ['dataMin - 1', 'dataMax + 1'];
        }
    };

    return (
        <div style={{ width: '100%', height: '400px' }}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart
                    data={chartData}
                    margin={{
                        top: 20,
                        right: 30,
                        left: 20,
                        bottom: 5,
                    }}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <Legend
                        verticalAlign="top"
                        height={36}
                        iconType="line"
                        wrapperStyle={{
                            paddingTop: '10px',
                            paddingBottom: '40px'
                        }}
                    />
                    <XAxis
                        dataKey="year"
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                        axisLine={false}
                    />
                    <YAxis
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                        axisLine={false}
                        domain={getYAxisDomain()}
                    />
                    <Tooltip
                        contentStyle={{
                            backgroundColor: '#fff',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                        }}
                    />

                    {isMultiLine ? (
                        // Multi-line chart - dynamically render lines based on data properties
                        getLineNames().map((lineKey) => (
                            <Line
                                key={lineKey}
                                type="monotone"
                                dataKey={lineKey}
                                stroke={colors[lineKey] || '#6b7280'}
                                strokeWidth={2}
                                dot={{ fill: colors[lineKey] || '#6b7280', strokeWidth: 2, r: 4 }}
                                activeDot={{ r: 6 }}
                                name={getDisplayName(lineKey)}
                                connectNulls={false}
                            />
                        ))
                    ) : (
                        // Single-line chart
                        <Line
                            type="monotone"
                            dataKey="value"
                            stroke={colors.value}
                            strokeWidth={2}
                            dot={{ fill: colors.value, strokeWidth: 2, r: 4 }}
                            activeDot={{ r: 6 }}
                            name="Score"
                            connectNulls={false}
                        />
                    )}
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

export default ChartDisplay;
